import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CloudOff, Loader2, Menu, PanelLeftClose, PanelLeftOpen, PartyPopper, PauseCircle, BookOpen, Flame } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader } from "@/components/Loader";
import { ChapterList, StatusDot } from "@/components/ChapterList";
import { NoteBox } from "@/components/NoteBox";
import { WritingEditor } from "@/components/WritingEditor";
import { ChapterBody, FullWork } from "@/components/FullWork";
import { WorkDetails } from "@/components/WorkDetails";
import { GoalRing } from "@/components/GoalRing";
import { StreakBadge } from "@/components/StreakBadge";
import { WorkNotes, WorkNotesButton } from "@/components/WorkNotes";
import { PagedFrame, PageWidth } from "@/components/Paged";
import { KindBadge, RatingBadge, StatusLine } from "@/components/WorkCard";
import { api, formatApiError } from "@/lib/api";
import { useAutosave } from "@/lib/useAutosave";
import { breakProps } from "@/lib/theme";
import { useAppearance } from "@/context/AppearanceContext";
import { CH_STATUS, firstGoalHit, fmt, localDay, overallStatus, wordsIn } from "@/lib/writing";

const STATUS = { saving: [Loader2, "Saving…", "text-slate-400", "animate-spin"], saved: [null, "Saved", "text-emerald-300/80", ""], offline: [CloudOff, "Offline – retrying", "text-amber-300", ""] };
const SaveStatus = ({ status }) => {
  const [Icon, label, cls, spin] = STATUS[status];
  return (
    <AnimatePresence mode="wait">
      <motion.span key={status} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }}
        className={`inline-flex items-center gap-1.5 font-code text-[11px] ${cls}`} data-testid="save-status">{Icon && <Icon className={`w-3 h-3 ${spin}`} />}{label}</motion.span>
    </AnimatePresence>
  );
};

const Tabs = ({ tab, setTab }) => (
  <div className="order-last w-full sm:order-none sm:w-auto flex justify-center pb-2 sm:pb-0"><div className="flex shrink-0 whitespace-nowrap rounded-full border border-line p-0.5 text-xs">
    {[["write", "Write"], ["full", "Full Work"]].map(([k, l]) => (
      <button key={k} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 transition-colors ${tab === k ? "bg-amber-500 text-onaccent font-semibold" : "text-slate-400 hover:text-slate-200"}`} data-testid={`work-tab-${k}`}>{l}</button>
    ))}
  </div></div>
);

export default function WorkPage() {
  const { workId } = useParams();
  const [work, setWork] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("write");
  const [stats, setStats] = useState({ today: 0, goal: 500 });
  const [pages, setPages] = useState([]);
  const [side, setSide] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [del, setDel] = useState(null);
  const [counts, setCounts] = useState({});
  const [notesOpen, setNotesOpen] = useState(false);
  const look = useAppearance();
  const [mainEl, setMainEl] = useState(null);
  const [mainW, setMainW] = useState(null);
  useEffect(() => {
    if (!mainEl) return;
    const ro = new ResizeObserver(([e]) => setMainW(e.contentRect.width));
    ro.observe(mainEl);
    return () => ro.disconnect();
  }, [mainEl]);
  const setCount = useCallback((id, n) => setCounts((m) => (m[id] === n ? m : { ...m, [id]: n })), []);
  const live = useRef({});
  const htmls = useRef({});
  const notes = useRef({});
  live.current = { chapters, goal: stats.goal };
  const onSaved = useCallback((d) => {
    setChapters((cs) => cs.map((c) => (c.id === d.id ? { ...c, words: d.words } : c)));
    if (firstGoalHit(d.today, live.current.goal)) {
      toast.success(`Daily goal reached: ${fmt(d.today)} words today!`, { icon: <PartyPopper className="w-4 h-4 text-amber-400" /> });
      api.get("/writing/stats", { params: { day: localDay() } }).then((r) => { setStats(r.data); toast.success(`Streak: ${r.data.streak} ${r.data.streak === 1 ? "day" : "days"}`, { icon: <Flame className="w-4 h-4 text-orange-400" /> }); });
    }
    setStats((s) => ({ ...s, today: d.today }));
  }, []);
  const { status, queue, flush } = useAutosave(onSaved);

  useEffect(() => {
    api.get(`/writing/works/${workId}`).then(({ data }) => {
      setWork(data);
      setChapters(data.chapters);
      setActive(data.chapters.some((c) => c.id === data.last_chapter_id) ? data.last_chapter_id : data.chapters[0]?.id);
    }).catch(() => setWork(false));
    api.get("/writing/stats", { params: { day: localDay() } }).then((r) => setStats(r.data));
  }, [workId]);
  useEffect(() => {
    if (!work?.world_id) return setPages([]);
    api.get(`/worlds/${work.world_id}/pages`, { params: work.profile_id ? { profile: work.profile_id } : {} }).then((r) => setPages(r.data.map((p) => ({ id: p.id, title: p.title, category: p.category })))).catch(() => setPages([]));
  }, [work?.world_id, work?.profile_id]);

  if (work === null) return <Loader />;
  if (work === false) return <div className="relative z-10 min-h-screen flex flex-col items-center justify-center gap-4" data-testid="work-not-found"><p className="font-display text-3xl text-amber-100">This work isn't here.</p><Link to="/writing" className="text-amber-300 text-sm">Back to your works</Link></div>;

  const cur = chapters.find((c) => c.id === active);
  const pick = (id) => { flush(); setActive(id); setTab("write"); setDrawer(false); };
  const edit = (html, text) => {
    htmls.current[active] = html;
    const n = wordsIn(text);
    if (n !== live.current.chapters.find((c) => c.id === active)?.words) setChapters((cs) => cs.map((c) => (c.id === active ? { ...c, words: n } : c)));
    queue(active, { html });
  };
  const editNote = (key) => (html) => { notes.current[active] = { ...notes.current[active], [key]: html }; queue(active, { [key]: html }); };
  const noteOf = (c, key) => notes.current[c.id]?.[key] ?? c[key] ?? "";
  const withHtml = chapters.map((c) => ({ ...c, html: htmls.current[c.id] ?? c.html, begin_note: noteOf(c, "begin_note"), end_note: noteOf(c, "end_note") }));
  const setChStatus = async (c, status) => {
    setChapters((cs) => cs.map((x) => (x.id === c.id ? { ...x, status } : x)));
    try { await api.put(`/writing/chapters/${c.id}`, { status }); } catch (e) { toast.error(formatApiError(e)); }
  };
  const hiatus = async () => {
    try { const { data } = await api.put(`/writing/works/${work.id}/hiatus`, { hiatus: !work.hiatus }); setWork({ ...work, hiatus: data.hiatus }); toast.success(data.hiatus ? "Marked as on hiatus" : "Back from hiatus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const ov = overallStatus(work.hiatus, chapters);
  const sb = breakProps(look.app && look.design(look.app.is_pro ? work.scene_break || look.app.scene_break : "default"));
  const fontVar = { "--work-font": work.font ? `"${work.font}"` : undefined, ...sb.style };
  const startOf = (id) => 1 + chapters.slice(0, chapters.findIndex((c) => c.id === id)).reduce((n, c) => n + (counts[c.id] || 1), 0);
  const togglePaged = async () => { const paged = !work.paged; setWork({ ...work, paged }); api.put(`/writing/works/${work.id}/paged`, { paged }).catch((e) => toast.error(formatApiError(e))); };
  const add = async () => {
    await flush();
    try { const { data } = await api.post(`/writing/works/${work.id}/chapters`, {}); setChapters((cs) => [...cs, data]); pick(data.id); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rename = async (c, title) => {
    setChapters((cs) => cs.map((x) => (x.id === c.id ? { ...x, title } : x)));
    try { await api.put(`/writing/chapters/${c.id}`, { title }); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    const c = del;
    setDel(null);
    await flush();
    try {
      await api.delete(`/writing/chapters/${c.id}`);
      const rest = chapters.filter((x) => x.id !== c.id);
      setChapters(rest);
      if (c.id === active) setActive(rest[Math.max(0, chapters.findIndex((x) => x.id === c.id) - 1)].id);
      toast.success(`"${c.title}" deleted`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const drop = () => setTimeout(() => api.put(`/writing/works/${work.id}/order`, { chapter_ids: live.current.chapters.map((c) => c.id) })
    .catch((e) => toast.error(formatApiError(e))), 0);
  const list = <ChapterList chapters={chapters} activeId={active} onPick={pick} onAdd={add} onRename={rename} onDelete={setDel} onReorder={(ids) => setChapters((cs) => ids.map((id) => cs.find((c) => c.id === id)))} onDrop={drop} onStatus={setChStatus} />;
  const total = chapters.reduce((n, c) => n + c.words, 0);

  return (
    <div className="relative z-10 min-h-screen flex flex-col" data-testid="work-page">
      <header className="sticky top-0 z-30 flex flex-wrap sm:flex-nowrap items-center gap-x-2 sm:gap-x-3 px-4 md:px-6 min-h-14 sm:h-14 border-b border-line bg-canvas/90 backdrop-blur-md">
        <Link to="/writing" className="p-1.5 text-slate-400 hover:text-amber-300" title="Your works" data-testid="work-back-btn"><ArrowLeft className="w-4 h-4" /></Link>
        <button onClick={() => setDrawer(true)} className="md:hidden p-1.5 text-slate-400" data-testid="chapters-drawer-btn"><Menu className="w-4 h-4" /></button>
        <button onClick={() => setSide(!side)} className="hidden md:block p-1.5 text-slate-500 hover:text-slate-200" title="Toggle chapters" data-testid="chapters-toggle-btn">{side ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}</button>
        <div className="min-w-0 flex-1 overflow-hidden py-1.5 sm:py-0">
          <p className="font-display text-lg text-amber-50 truncate leading-tight" data-testid="work-title">{work.title}</p>
          <div className="flex items-center gap-2"><span className="hidden sm:contents"><KindBadge w={work} /><RatingBadge rating={work.rating} /></span><StatusLine w={{ status: ov.status }} total={chapters.length} published={ov.published} compact />
            {work.world_name && <span className="hidden sm:inline text-[10px] font-code text-slate-500 truncate">· {work.world_name}</span>}</div>
        </div>
        <button onClick={hiatus} title={work.hiatus ? "Turn off hiatus" : "Mark as on hiatus"} aria-pressed={!!work.hiatus}
          className={`hidden sm:inline-flex shrink-0 items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 transition-colors ${work.hiatus ? "border-violet-400/50 bg-violet-500/10 text-violet-200" : "border-line text-slate-400 hover:text-violet-200"}`} data-testid="work-hiatus-btn">
          <PauseCircle className="w-3.5 h-3.5" /><span className="hidden xl:inline">{work.hiatus ? "On hiatus" : "Hiatus"}</span></button>
        <button onClick={togglePaged} aria-pressed={!!work.paged} title="Show text on book pages"
          className={`inline-flex shrink-0 items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 transition-colors ${work.paged ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-line text-slate-400 hover:text-amber-200"}`} data-testid="work-paged-btn">
          <BookOpen className="w-3.5 h-3.5" /><span className="hidden xl:inline">Paged</span></button>
        <Tabs tab={tab} setTab={(t) => { flush(); setTab(t); }} />
        <div className="hidden lg:flex items-center gap-2"><StreakBadge stats={stats} /><GoalRing today={stats.today} goal={stats.goal} onGoal={(goal) => setStats({ ...stats, goal })} compact /></div>
        <WorkNotesButton open={notesOpen} onClick={() => setNotesOpen(!notesOpen)} />
        <WorkDetails work={work} onSaved={(d) => setWork({ ...work, ...d, chapters: undefined })} />
      </header>
      <div className="flex flex-1 min-h-0">
        {side && <aside className="hidden md:block w-64 shrink-0 border-r border-line p-3 overflow-y-auto sticky top-14 h-[calc(100vh-3.5rem)]" data-testid="chapter-sidebar">{list}</aside>}
        <main ref={setMainEl} className="relative flex-1 min-w-0" style={fontVar} data-sb={sb["data-sb"]} data-testid="work-main"><PageWidth.Provider value={mainW}>
          {tab === "write" && cur ? (
            <>
              <div className="w-full px-6 sm:px-10 lg:px-16 pt-8 pb-4">
                <div className="flex items-center gap-2">
                  <p className="font-code text-[10px] uppercase tracking-[0.3em] text-amber-500/70">Chapter {chapters.indexOf(cur) + 1}</p>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-400"><StatusDot status={cur.status} onChange={(st) => setChStatus(cur, st)} testId="editor-chapter-status" />
                    <span data-testid="editor-chapter-status-label">{CH_STATUS.find((x) => x[0] === cur.status)?.[1] || "In progress"}</span></span>
                </div>
                <h2 className="font-display text-3xl text-amber-100" data-testid="chapter-heading">{cur.title}</h2>
              </div>
              <NoteBox key={`b-${cur.id}`} label="Beginning note" value={noteOf(cur, "begin_note")} onChange={editNote("begin_note")} testId="begin-note" />
              <WritingEditor key={cur.id} value={htmls.current[cur.id] ?? cur.html} onChange={edit} pages={pages} worldId={work.world_id} font={work.font}
                {...(work.paged ? { wrap: (x) => <PagedFrame size={work.page_size} startPage={startOf(cur.id)} onPages={(n) => setCount(cur.id, n)}>{x}</PagedFrame> } : {})} />
              {work.paged && <div aria-hidden className="absolute left-0 top-0 w-0 h-0 overflow-hidden invisible">
                {withHtml.slice(0, chapters.indexOf(cur)).map((c) => <PagedFrame key={`${c.id}-${work.page_size}`} size={work.page_size} onPages={(n) => setCount(c.id, n)} testId="paged-measure"><ChapterBody c={c} /></PagedFrame>)}
              </div>}
              <div className="pb-16"><NoteBox key={`e-${cur.id}`} label="End note" value={noteOf(cur, "end_note")} onChange={editNote("end_note")} testId="end-note" /></div>
            </>
          ) : <FullWork work={work} chapters={withHtml} onJump={pick} counts={counts} onCount={setCount} />}
        </PageWidth.Provider></main>
        <WorkNotes workId={work.id} open={notesOpen} onOpenChange={setNotesOpen} />
      </div>
      <footer className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 md:px-6 py-2 border-t border-line bg-canvas/90 backdrop-blur-md font-code text-[11px] text-slate-500" data-testid="work-footer">
        <SaveStatus status={status} />
        <span className="flex gap-4">
          {tab === "write" && cur && <span data-testid="count-chapter">{fmt(cur.words)} in chapter</span>}
          <span data-testid="count-work">{fmt(total)} in work</span>
          <span className="text-amber-300/80" data-testid="count-today">{fmt(stats.today)} today</span>
        </span>
      </footer>
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="bg-panel border-line w-72 p-4" data-testid="chapters-drawer"><SheetTitle className="sr-only">Chapters</SheetTitle><div className="mt-6">{list}</div></SheetContent>
      </Sheet>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-panel border-line" data-testid="chapter-delete-dialog">
          <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{del?.title}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">{fmt(del?.words)} words will be removed. This can't be undone.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="chapter-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="chapter-delete-confirm">Delete chapter</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
